import Text from "@src/lib/ui/Text";
import { FC, useMemo } from "react";
import { gql } from "@apollo/client";
import {
  useMyTrainingAssignmentsTableQuery,
  useMyTrainingAssignmentsTableLazyQuery,
  MyTrainingAssignmentsTable_AssignmentFragment,
} from "./MyTrainingAssignmentsTable.generated";
import { useRouter } from "next/router";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";
import { LibraryItemType } from "@src/types.generated";
import { formatDateShort } from "@src/features/myTraining/utils";
import { DateTime } from "luxon";
import usePublishUpdatedContentFeatureFlag from "@src/features/myTraining/hooks/usePublishUpdatedContentFeatureFlag";
import { useMyTrainingPageContentTranslations } from "@src/features/myTraining/hooks/useMyTrainingPageContentTranslations";

type Column =
  | "name"
  | "type"
  | "assignedAt"
  | "totalAttempts"
  | "startedAt"
  | "dueDate";

type Props = {
  filterBarContainer?: HTMLElement | null;
};

const MyTrainingAssignmentsTable: FC<Props> = ({ filterBarContainer }) => {
  const publishUpdatedContentFeatureFlag =
    usePublishUpdatedContentFeatureFlag();
  const router = useRouter();
  const translations = useMyTrainingPageContentTranslations();
  const allColumns = useMemo((): Column[] => {
    if (publishUpdatedContentFeatureFlag) {
      return [
        "name",
        "type",
        "assignedAt",
        "totalAttempts",
        "startedAt",
        "dueDate",
      ];
    } else {
      return ["name", "type", "startedAt", "dueDate"];
    }
  }, [publishUpdatedContentFeatureFlag]);

  return (
    <PaginatedTable<
      MyTrainingAssignmentsTable_AssignmentFragment,
      Column,
      typeof useMyTrainingAssignmentsTableQuery,
      typeof useMyTrainingAssignmentsTableLazyQuery,
      Column
    >
      input={{}}
      pollInterval={1000 * 60}
      queryHook={useMyTrainingAssignmentsTableQuery}
      lazyQueryHook={useMyTrainingAssignmentsTableLazyQuery}
      getItems={(data) => data?.MyTodoAssignments?.objects ?? []}
      getTotalItemCount={(data) => data?.MyTodoAssignments?.totalCount ?? 0}
      noDataEmptyState={
        <div className="flex flex-1 self-stretch">
          <EmptyState>
            <EmptyStateIcon>
              <CheckmarkIcon className="size-16" />
            </EmptyStateIcon>
            <EmptyStateTitle>
              {translations.noIncompleteAssignments}
            </EmptyStateTitle>
          </EmptyState>
        </div>
      }
      hideCount
      filterBarContainer={filterBarContainer}
      horizontalScroll
      stickyFirstColumn
      resizableColumns
      columnWidthPersistenceKey="my-training-assignments-v1"
      trailingEmptyColumnWidth={16}
      growableColumn="name"
      allColumns={allColumns}
      defaultSort={{ column: "dueDate", descending: false, nullsLast: true }}
      exportConfig={{
        title: "my-training.csv",
        exportColumns: () => allColumns,
        exportColumnTitles: {
          name: translations.name,
          type: translations.type,
          assignedAt: translations.assignedAt,
          totalAttempts: translations.totalAttempts,
          startedAt: translations.startedAt,
          dueDate: translations.dueDate,
        },
        exportFns: {
          name: (item) => item.libraryItem.name.myTranslation,
          type: (item) => {
            if (item.libraryItem.type === LibraryItemType.Module)
              return translations.module;
            if (item.libraryItem.type === LibraryItemType.ScormCourse)
              return translations.scormCourse;
            return translations.course;
          },
          assignedAt: (item) => {
            const sorted = [...item.accessSources].sort((a, b) =>
              DateTime.fromISO(b.createdAt)
                .diff(DateTime.fromISO(a.createdAt))
                .toMillis(),
            );
            return formatDateShort(sorted[0]?.createdAt) ?? "";
          },
          totalAttempts: (item) => item.attemptsCount,
          startedAt: (item) =>
            formatDateShort(
              item.currentCourseInstance?.startedAt ||
                item.currentModuleInstance?.startedAt ||
                item.currentSCORMCourseInstance?.startedAt,
            ) ?? "",
          dueDate: (item) =>
            formatDateShort(
              item.currentCourseInstance?.dueDate ||
                item.currentModuleInstance?.dueDate ||
                item.currentSCORMCourseInstance?.dueDate,
            ) ?? "",
        },
      }}
      columnTitles={{
        name: translations.name,
        type: translations.type,
        assignedAt: translations.assignedAt,
        totalAttempts: translations.totalAttempts,
        startedAt: translations.startedAt,
        dueDate: translations.dueDate,
      }}
      responsiveness={{
        name: {
          minWidth: 240,
          flex: 3,
        },
        type: {
          minWidth: 100,
        },
        assignedAt: {
          minWidth: 120,
        },
        totalAttempts: {
          minWidth: 120,
        },
        startedAt: {
          minWidth: 120,
        },
        dueDate: {
          minWidth: 120,
        },
      }}
      onRowClick={(item) => {
        if (item.currentCourseInstance) {
          router.push({
            pathname: "/my-training/course-instance/[id]",
            query: { id: String(item.currentCourseInstance.id) },
          });
        } else if (item.currentModuleInstance) {
          router.push({
            pathname: "/my-training/module-instance/[id]",
            query: { id: String(item.currentModuleInstance.id) },
          });
        } else if (item.currentSCORMCourseInstance) {
          router.push({
            pathname: "/my-training/scorm-course-instance/[id]",
            query: { id: String(item.currentSCORMCourseInstance.id) },
          });
        }
      }}
      renderFns={{
        name: (item) => (
          <div className="flex items-center gap-2">
            <ContentAvatar
              size={32}
              contentType="Module"
              style={{ borderRadius: 8 }}
              coverImage={
                item.libraryItem.coverImage || item.libraryItem.coverImage
              }
              defaultBackgroundHashKey={item.libraryItem.id}
            />
            <Text type="P2">{item.libraryItem.name.myTranslation}</Text>
          </div>
        ),
        type: (item) => {
          if (item.libraryItem.type === LibraryItemType.Module) {
            return translations.module;
          } else if (item.libraryItem.type === LibraryItemType.ScormCourse) {
            return translations.scormCourse;
          }
          return translations.course;
        },
        assignedAt: (item) => {
          const sortedAccessSources = [...item.accessSources].sort((a, b) =>
            DateTime.fromISO(b.createdAt)
              .diff(DateTime.fromISO(a.createdAt))
              .toMillis(),
          );
          const assignedAt = sortedAccessSources[0].createdAt;
          return formatDateShort(assignedAt);
        },
        totalAttempts: (item) => item.attemptsCount,
        startedAt: (item) =>
          formatDateShort(
            item.currentCourseInstance?.startedAt ||
              item.currentModuleInstance?.startedAt ||
              item.currentSCORMCourseInstance?.startedAt,
          ),
        dueDate: (item) =>
          formatDateShort(
            item.currentCourseInstance?.dueDate ||
              item.currentModuleInstance?.dueDate ||
              item.currentSCORMCourseInstance?.dueDate,
          ),
      }}
      itemId={(item) => item.id}
    />
  );
};

export default MyTrainingAssignmentsTable;

gql`
  query MyTrainingAssignmentsTable(
    $input: MyTodoAssignmentsInput!
    $pagination: PaginationInput
  ) {
    MyTodoAssignments(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...MyTrainingAssignmentsTable_Assignment
      }
    }
  }
  fragment MyTrainingAssignmentsTable_Assignment on Assignment {
    id
    status
    attemptsCount
    accessSources {
      id
      createdAt
    }
    libraryItem {
      ...MyTrainingAssignmentsTable_LibraryItem
    }
    currentCourseInstance {
      ...MyTrainingAssignmentsTable_CourseInstance
    }
    currentModuleInstance {
      ...MyTrainingAssignmentsTable_ModuleInstance
    }
    currentSCORMCourseInstance {
      ...MyTrainingAssignmentsTable_SCORMCourseInstance
    }
  }
  fragment MyTrainingAssignmentsTable_LibraryItem on LibraryItem {
    id
    type
    name {
      ...MyTranslationSet
    }
    coverImage {
      ...CoverImage
    }
    description {
      ...MyTranslationSet
    }
  }
  fragment MyTrainingAssignmentsTable_ModuleInstance on ModuleInstance {
    id
    startedAt
    completedAt
    percentComplete
    dueDate
  }
  fragment MyTrainingAssignmentsTable_CourseInstance on CourseInstance {
    id
    track
    startedAt
    completedAt
    percentComplete
    dueDate
    certificateUrl
  }
  fragment MyTrainingAssignmentsTable_SCORMCourseInstance on SCORMCourseInstance {
    id
    startedAt
    completedAt
    dueDate
  }
`;
